import {
  FindTargetBreakdownsData,
  FindTargetsData,
  TargetBreakdownChildrenRequest,
} from '../../../../../../services/modules/target/target.repository.types';
import { TargetUnit } from '../../../../../../services/modules/target/target.types';
import {
  TARGET_EMPTY_VALUE,
  TARGET_TYPE_BY_RECIPIENT_TYPE,
} from '../../../../../target/target.constants';
import {
  BuildOrderTargetItemRowsParams,
  BuildOrderTargetRowsParams,
  GetBreakdownChildrenRequestsParams,
  GetExpandableIdsParams,
  GetRequestedBreakdownQueriesParams,
  MergeOrderTargetBreakdownsParams,
  OrderTargetBreakdowns,
  OrderTargetRow,
  OrderTargetRowKind,
  ToOrderTargetItemRowParams,
  ToOrderTargetRowParams,
} from './orderObjectTargetsTab.types';

type ChildrenByParentId = Map<string | null, FindTargetBreakdownsData[]>;

export const getOrderTargetUnits = (targets: Pick<FindTargetsData, 'unit'>[]): TargetUnit[] => [
  ...new Set(targets.map((target) => target.unit)),
];

export const getExpandableIds = ({ targets, breakdowns }: GetExpandableIdsParams): Set<string> => {
  const itemsByTargetId = new Map(
    breakdowns.map((breakdown) => [breakdown.targetId, breakdown.items ?? []]),
  );

  return new Set(
    targets.flatMap((target) => {
      const items = itemsByTargetId.get(target.id) ?? [];

      return [
        ...(items.length > 0 ? [target.id] : []),
        ...items.filter((item) => item.hasChildren).map((item) => item.id),
      ];
    }),
  );
};

export const getBreakdownChildrenRequests = ({
  expandedIds,
  itemTargetIds,
  treeTargetIds,
}: GetBreakdownChildrenRequestsParams): TargetBreakdownChildrenRequest[] =>
  [...itemTargetIds]
    .filter(([itemId, targetId]) => expandedIds.has(itemId) && !treeTargetIds.has(targetId))
    .map(([parentId, targetId]) => ({ targetId, parentId }));

export const getRequestedBreakdownQueries = <Query extends OrderTargetBreakdowns>({
  firstLevels,
  trees,
  children,
  treeTargetIds,
}: GetRequestedBreakdownQueriesParams<Query>): Query[] => [
  ...firstLevels,
  ...trees.filter((tree) => treeTargetIds.has(tree.targetId)),
  ...children,
];

export const mergeOrderTargetBreakdowns = ({
  firstLevels,
  trees,
  children,
  treeTargetIds,
}: MergeOrderTargetBreakdownsParams): OrderTargetBreakdowns[] => {
  const treeItemsByTargetId = new Map(
    trees
      .filter((tree) => treeTargetIds.has(tree.targetId))
      .map((tree) => [tree.targetId, tree.items]),
  );
  const childItemsOf = (targetId: string) =>
    children.filter((child) => child.targetId === targetId).flatMap((child) => child.items ?? []);

  return firstLevels.map(({ targetId, items }) => ({
    targetId,
    items: treeItemsByTargetId.get(targetId) ?? (items && [...items, ...childItemsOf(targetId)]),
  }));
};

const getToggleDepths = (targets: Pick<FindTargetsData, 'dimensionCount'>[]): Set<number> =>
  new Set(
    targets.flatMap(({ dimensionCount }) =>
      Array.from({ length: Math.max(dimensionCount - 1, 0) }, (_, depth) => depth),
    ),
  );

const groupByParentId = (items: FindTargetBreakdownsData[]): ChildrenByParentId => {
  const childrenByParentId: ChildrenByParentId = new Map();

  items.forEach((item) => {
    const siblings = childrenByParentId.get(item.parentId) ?? [];
    siblings.push(item);
    childrenByParentId.set(item.parentId, siblings);
  });

  return childrenByParentId;
};

const childrenOf = (childrenByParentId: ChildrenByParentId, parentId: string | null) =>
  childrenByParentId.get(parentId) ?? [];

const toTargetRow = ({
  target,
  summary,
  hasChildren,
  isExpanded,
  isLoneTarget,
  hasToggleSlot,
}: ToOrderTargetRowParams): OrderTargetRow => {
  const recipientType = target.recipient?.type ?? null;

  return {
    id: target.id,
    kind: OrderTargetRowKind.TARGET,
    targetId: target.id,
    targetType: recipientType ? TARGET_TYPE_BY_RECIPIENT_TYPE[recipientType] : null,
    label: String(target.code),
    unit: target.unit,
    plannedValue: target.attainment.planned,
    plannedTotal: target.attainment.planned,
    attainedValue: target.attainment.attained,
    conversion: {
      attainedByStatus: target.attainment.attainedByStatus,
      conversion: target.attainment.conversion,
      calculatedAt: target.attainment.calculatedAt,
    },
    summary,
    hasChildren,
    isExpanded,
    hasToggleSlot,
    canToggle: hasChildren && !isLoneTarget,
  };
};

const toItemRow = ({
  target,
  item,
  parentRowId,
  hasChildren,
  isExpanded,
  hasToggleSlot,
}: ToOrderTargetItemRowParams): OrderTargetRow => ({
  id: item.id,
  parentId: parentRowId,
  kind: OrderTargetRowKind.ITEM,
  targetId: target.id,
  targetType: null,
  label: item.label ?? TARGET_EMPTY_VALUE,
  unit: target.unit,
  plannedValue: item.plannedValue,
  plannedTotal: target.attainment.planned,
  attainedValue: item.attained,
  conversion: {
    attainedByStatus: item.attainedByStatus,
    conversion: item.conversion,
    calculatedAt: target.attainment.calculatedAt,
  },
  hasChildren,
  isExpanded,
  hasToggleSlot,
  canToggle: hasChildren,
});

const buildItemRows = ({
  target,
  items,
  expandedIds,
  expandableDepths,
}: BuildOrderTargetItemRowsParams): OrderTargetRow[] => {
  const childrenByParentId = groupByParentId(items);
  const rows: OrderTargetRow[] = [];

  const visit = (parentId: string | null, parentRowId: string) => {
    for (const item of childrenOf(childrenByParentId, parentId)) {
      const { hasChildren } = item;
      const isExpanded = hasChildren && expandedIds.has(item.id);

      rows.push(
        toItemRow({
          target,
          item,
          parentRowId,
          hasChildren,
          isExpanded,
          hasToggleSlot: expandableDepths.has(item.depth),
        }),
      );

      if (isExpanded) {
        visit(item.id, item.id);
      }
    }
  };

  visit(null, target.id);

  return rows;
};

export const buildOrderTargetRows = ({
  targets,
  breakdowns,
  details,
  expandedIds,
}: BuildOrderTargetRowsParams): OrderTargetRow[] => {
  const itemsByTargetId = new Map(
    breakdowns.map((breakdown) => [breakdown.targetId, breakdown.items]),
  );
  const loadedItemsOf = (targetId: string) => itemsByTargetId.get(targetId) ?? [];
  const detailByTargetId = new Map(details.map((entry) => [entry.targetId, entry.detail]));
  const isLoneTarget = targets.length === 1;
  const hasTargetToggleSlot =
    !isLoneTarget && targets.some((target) => itemsByTargetId.get(target.id)?.length !== 0);
  const expandableDepths = getToggleDepths(targets);

  return targets.flatMap((target) => {
    const items = loadedItemsOf(target.id);
    const hasChildren = items.length > 0;
    const isExpanded = hasChildren && (isLoneTarget || expandedIds.has(target.id));
    const targetRow = toTargetRow({
      target,
      summary: detailByTargetId.get(target.id),
      hasChildren,
      isExpanded,
      isLoneTarget,
      hasToggleSlot: hasTargetToggleSlot,
    });

    if (!isExpanded) {
      return [targetRow];
    }

    return [targetRow, ...buildItemRows({ target, items, expandedIds, expandableDepths })];
  });
};
